import React, {useCallback, useEffect, useState} from 'react';
import {useAgentApi} from '../Services/AgentApiProvider';
import Table from './Tables';
import {formatDateUTC} from '../utils/formatDateUTC';
import "./SearchInput.css";
import AgentAuditOperationDetail from "./AgentAuditOperationDetail";
import DateRangeSelector, {getDateRangeByPeriod} from "./DateRangeSelector";

function AgentAuditOperation({agentId, detailAudit, setDetailAudit}) {
  const {getAgentAuditOperations, getAgentAuditOperationById} = useAgentApi();

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auditOperations, setAuditOperations] = useState([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchId, setSearchId] = useState("");
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const columns = [
    {name: 'ID', selector: (row) => row.id, sortable: true},
    {name: 'Duration', selector: (row) => row.duration, sortable: true},
    {name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true},
    {name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true},
  ];

  const handlePeriodChange = useCallback((period) => {
    setStartDate(period?.start_date);
    setEndDate(period?.end_date);
  }, []);

  const handleEditClick = async (row) => {
    try {
      setError(null);
      setDetailLoading(true);

      const detail = await getAgentAuditOperationById(agentId, row.id);

      if (!detail) {
        setError("Failed to load audit operation detail");
        return;
      }

      setDetailAudit(detail);
    } catch (error) {
      console.error("Error fetching audit operation detail:", error);
      setError("Failed to load audit operation detail");
    } finally {
      setDetailLoading(false);
    }
  }

  const fetchAuditOperations = async (skip, limit, idFilter = null) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {

      let start = startDate;
      let end = endDate;

      if (!start || !end) {
        const range = getDateRangeByPeriod("day");
        start = range.start_date;
        end = range.end_date;
      }

      const filterById = idFilter !== null ? idFilter : searchId;

      setLoading(true);
      const response = await getAgentAuditOperations(agentId, skip, limit, start, end, filterById || null);
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
      setItemsPerPage(limit);
    } catch (error) {
      console.error("Error fetching audit operations:", error);
      setError("Failed to load audit operations");
    } finally {
      setLoading(false);
    }
  }

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchId(value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      fetchAuditOperations(0, itemsPerPage, searchId);
    }
  };

  const handleSearchClear = () => {
    setSearchId("");
    fetchAuditOperations(0, itemsPerPage, "");
  };

  useEffect(() => {
    fetchAuditOperations(0, itemsPerPage);
  }, [startDate, endDate]);


  return (
    <div className="radius-yes p-2 bg-yes col-12">
      {detailAudit ? (
        <>
          {detailLoading ? (
            <div>Loading...</div>
          ) : (
            <AgentAuditOperationDetail
              agentAudit={detailAudit}
              onClose={() => setDetailAudit(null)}
            />
          )}
        </>
      ) : (
        <>
          <div className={"mb-3 p-2"}>
            <DateRangeSelector onChange={handlePeriodChange}/>
            <div className="search mt-2">
              <div className="search-wrapper">
                <div className="icon-wrapper">
                  <i className="fa-solid fa-magnifying-glass"></i>
                </div>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by ID..."
                  value={searchId}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                />
                {searchId && (
                  <div className="icon-wrapper" style={{cursor: 'pointer'}} onClick={handleSearchClear}>
                    <i className="fa-solid fa-times"></i>
                  </div>
                )}
              </div>
            </div>
          </div>
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Audit Operations"
                columns={columns}
                data={auditOperations}
                onRowClicked={handleEditClick}
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                paginationTotalRows={totalAuditOperations}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchAuditOperations}
              />
            </>
          )}
        </>)}
    </div>
  );
}

export default AgentAuditOperation;
